import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiClock, FiArrowRight, FiTrendingUp, FiTarget, FiShield } = FiIcons;

const Blog = () => {
  const featuredPost = {
    title: 'Navigating JSE Listing Requirements for Emerging Companies',
    excerpt: 'A comprehensive guide to understanding the regulatory landscape and strategic considerations for companies planning to list on the Johannesburg Stock Exchange.',
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    category: 'Strategic Planning',
    date: '2024-01-15',
    readTime: '8 min read',
    slug: 'jse-listing-requirements-guide'
  };

  const blogPosts = [
    {
      title: 'Digital Transformation Trends in South African Manufacturing',
      excerpt: 'Exploring how local manufacturers are leveraging technology to improve efficiency while addressing infrastructure challenges.',
      image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      category: 'Digital Transformation',
      date: '2024-01-10',
      readTime: '6 min read',
      slug: 'digital-transformation-manufacturing'
    },
    {
      title: 'BEE Strategy Beyond Compliance: Creating Competitive Advantage',
      excerpt: 'How progressive companies are turning BEE requirements into strategic opportunities for growth and market expansion.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      category: 'Strategy',
      date: '2024-01-05',
      readTime: '7 min read',
      slug: 'bee-strategy-competitive-advantage'
    },
    {
      title: 'Risk Management in Volatile Economic Environments',
      excerpt: 'Strategies for building resilience and maintaining operational excellence during economic uncertainty.',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      category: 'Risk Management',
      date: '2023-12-28',
      readTime: '5 min read',
      slug: 'risk-management-volatile-economy'
    },
    {
      title: 'Leadership Development for Multi-Cultural Teams',
      excerpt: 'Best practices for developing leadership capabilities in South Africa\'s diverse business environment.',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      category: 'Leadership',
      date: '2023-12-22',
      readTime: '6 min read',
      slug: 'leadership-multicultural-teams'
    },
    {
      title: 'Supply Chain Resilience in Post-Pandemic South Africa',
      excerpt: 'Lessons learned and strategies for building robust supply chains that can withstand future disruptions.',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      category: 'Operations',
      date: '2023-12-15',
      readTime: '8 min read',
      slug: 'supply-chain-resilience'
    },
    {
      title: 'Technology Adoption Strategies for Traditional Industries',
      excerpt: 'How established industries can successfully integrate new technologies without disrupting core operations.',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      category: 'Digital Transformation',
      date: '2023-12-08',
      readTime: '7 min read',
      slug: 'technology-adoption-traditional-industries'
    }
  ];

  const categories = [
    { name: 'All', count: 12, icon: null },
    { name: 'Strategic Planning', count: 4, icon: FiTarget },
    { name: 'Digital Transformation', count: 3, icon: FiTrendingUp },
    { name: 'Risk Management', count: 2, icon: FiShield },
    { name: 'Leadership', count: 2, icon: null },
    { name: 'Operations', count: 1, icon: null }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 font-serif">
              Insights & Analysis
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Strategic insights, market analysis, and thought leadership on navigating South Africa's dynamic business landscape.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl shadow-xl overflow-hidden"
          >
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="relative h-64 lg:h-auto">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </span>
                </div>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    {featuredPost.category}
                  </span>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>{formatDate(featuredPost.date)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiClock} className="w-4 h-4" />
                    <span>{featuredPost.readTime}</span>
                  </div>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 font-serif">
                  {featuredPost.title}
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                <Link
                  to={`/blog/${featuredPost.slug}`}
                  className="inline-flex items-center text-purple-700 font-semibold hover:text-purple-800 transition-colors"
                >
                  Read Full Article
                  <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories & Posts */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Categories Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Categories</h3>
                <ul className="space-y-3">
                  {categories.map((category, index) => (
                    <li key={index}>
                      <button className="flex items-center justify-between w-full text-left py-2 px-3 rounded-lg hover:bg-purple-50 transition-colors group">
                        <div className="flex items-center space-x-3">
                          {category.icon && (
                            <SafeIcon icon={category.icon} className="w-4 h-4 text-purple-600" />
                          )}
                          <span className="text-gray-700 group-hover:text-purple-700">
                            {category.name}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {category.count}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Blog Posts Grid */}
            <div className="lg:col-span-3">
              <div className="grid md:grid-cols-2 gap-8">
                {blogPosts.map((post, index) => (
                  <motion.article
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
                  >
                    <div className="relative h-48">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white text-purple-700 px-2 py-1 rounded text-sm font-medium">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                          <span>{formatDate(post.date)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiClock} className="w-4 h-4" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-purple-700 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {post.excerpt}
                      </p>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center text-purple-700 font-medium hover:text-purple-800 transition-colors"
                      >
                        Read More
                        <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>

              {/* Load More */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mt-12"
              >
                <button className="bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-colors">
                  Load More Articles
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 font-serif">
              Stay Informed
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Subscribe to receive our latest insights on South African business strategy and market developments directly in your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
              />
              <button
                type="submit"
                className="bg-yellow-400 text-purple-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-300 transition-colors"
              >
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;